import { Product } from './types';
import { Languages, FileCheck, Users, Award, Mic, PenTool, Volume2, BookOpen, Globe } from 'lucide-react';

export const languageTestingProducts: Product[] = [
  {
    id: 'complete-language-assessment-certification',
    title: 'Complete Language Assessment & Certification Platform',
    subtitle: 'Comprehensive language evaluation',
    description: 'All-in-one language assessment platform combining CEFR assessment with comprehensive proficiency testing. Features adaptive testing, multi-skill evaluation, and certification management.',
    icon: Award,
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    businessTypes: ['#education', '#language-schools', '#certification', '#corporate-training', '#hr-departments'],
    features: [
      'CEFR-Aligned Assessment Framework & Standards',
      'European Language Standards Compliance',
      'Adaptive Testing & Personalized Assessments',
      'Multi-Skill Language Evaluation System',
      'CEFR Level Determination & Placement',
      'Automated CEFR Scoring & Classification',
      'CEFR Progress Tracking & Learning Paths',
      'CEFR Certification & Badge Management',
      'European Assessment Reporting Standards',
      'CEFR Integration with Learning Systems',
      'Multi-Language CEFR Support',
      'CEFR Proctoring & Security Features',
      'Advanced Language Proficiency Assessment',
      'AI-Powered Language Evaluation Engine',
      'Speaking & Writing Assessment Tools',
      'Reading & Listening Comprehension Testing',
      'Grammar & Vocabulary Proficiency Testing',
      'Cultural Competency & Communication Skills',
      'Custom Proficiency Assessment Creation',
      'Instant Results & Detailed Feedback',
      'Performance Analytics & Learning Insights',
      'Business Language Proficiency Testing',
      'Academic Language Assessment',
      'Professional Certification Management',
      'Unified Assessment Dashboard & Analytics',
      'HR & Learning System Integration',
      'Multi-Device Assessment Support',
      'Advanced Security & Anti-Cheating',
      'Custom Assessment Workflow Creation',
      'API Integration & Third-Party Connectivity',
      'Advanced Reporting & Certification',
      'Mobile Language Assessment Platform'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'blue'
  },
  {
    id: 'complete-audio-language-assessment',
    title: 'Complete Audio Language Assessment Suite',
    subtitle: 'Comprehensive audio evaluation',
    description: 'Specialized audio-focused language assessment platform combining oral assessment with listening comprehension testing. Features speech recognition, pronunciation analysis, and audio comprehension evaluation.',
    icon: Mic,
    image: 'https://images.unsplash.com/photo-1598300042247-d088f8ab3a91?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    businessTypes: ['#language-testing', '#education', '#corporate-training', '#certification', '#audio-assessment'],
    features: [
      'Advanced Speech Recognition & Analysis',
      'Pronunciation Assessment & Scoring',
      'Fluency & Rhythm Evaluation',
      'Conversational Skill Testing & Simulation',
      'Interactive Speaking Tasks & Scenarios',
      'Real-Time Audio Processing & Feedback',
      'Automated Oral Scoring System',
      'Detailed Pronunciation & Accent Analysis',
      'Speaking Progress Tracking & Improvement',
      'Oral Assessment Customization Tools',
      'Voice Quality & Clarity Assessment',
      'Mobile Oral Assessment Platform',
      'Interactive Audio Content Library & Management',
      'Dynamic Comprehension Question Generation',
      'Multi-Level Audio Difficulty Assessment',
      'Real-Time Audio Comprehension Analysis',
      'Contextual Understanding & Inference Testing',
      'Automated Listening Assessment Scoring',
      'Audio Progress Tracking & Analytics',
      'Custom Audio Content Upload & Management',
      'Multi-Accent & Dialect Audio Support',
      'Listening Skill Development Pathways',
      'Audio Assessment Accessibility Features',
      'Comprehensive Audio Performance Reports',
      'Unified Audio Assessment Dashboard',
      'Language Learning Platform Integration',
      'Multi-Device Audio Assessment Support',
      'Advanced Audio Security & Proctoring',
      'Custom Audio Workflow Creation',
      'API Integration & Connectivity',
      'Advanced Audio Analytics & Reporting',
      'Professional Audio Assessment Certification'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'orange'
  },
  {
    id: 'writing-evaluation-platform',
    title: 'Writing Evaluation Platform',
    subtitle: 'Automated writing assessment',
    description: 'Comprehensive writing evaluation platform with AI-powered scoring for essays, reports, and creative writing assessments.',
    icon: PenTool,
    image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    businessTypes: ['#education', '#writing-assessment', '#academic-testing', '#certification', '#corporate-training'],
    features: [
      'AI Writing Analysis Engine',
      'Grammar & Syntax Evaluation',
      'Content Quality Assessment',
      'Coherence & Organization Scoring',
      'Plagiarism Detection',
      'Style & Tone Analysis',
      'Automated Rubric Scoring',
      'Detailed Feedback Reports'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-orange-500 to-red-600'
  },
  {
    id: 'multilingual-competency-platform',
    title: 'Multilingual Competency Platform',
    subtitle: 'Global language skills assessment',
    description: 'Comprehensive multilingual assessment platform supporting 50+ languages with cultural competency testing and international certification standards.',
    icon: Globe,
    image: 'https://images.unsplash.com/photo-1577563908411-5077b6dc7624?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    businessTypes: ['#multilingual', '#global-assessment', '#cultural-competency', '#international', '#certification'],
    features: [
      'Support for 50+ Languages',
      'Cultural Competency Assessment',
      'International Certification Standards',
      'Cross-Cultural Communication Testing',
      'Business Language Evaluation',
      'Regional Dialect Recognition',
      'Localized Assessment Content',
      'Global Scoring Standards',
      'Multi-Language Interface',
      'Cultural Context Analysis',
      'International Compliance',
      'Global Reporting Standards'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-green-500 to-blue-600'
  },
  {
    id: 'adaptive-language-testing-engine',
    title: 'Adaptive Language Testing Engine',
    subtitle: 'AI-driven personalized assessment',
    description: 'Advanced adaptive language testing platform that personalizes difficulty based on performance, providing accurate and efficient language proficiency evaluation.',
    icon: Languages,
    image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Education & Training',
    status: 'Available',
    businessTypes: ['#adaptive-testing', '#ai-assessment', '#personalized-learning', '#efficiency', '#accuracy'],
    features: [
      'AI-Powered Adaptive Algorithm',
      'Real-Time Difficulty Adjustment',
      'Personalized Question Selection',
      'Efficient Testing Protocols',
      'Accurate Proficiency Measurement',
      'Reduced Testing Time',
      'Optimized Question Bank',
      'Performance-Based Progression',
      'Smart Item Response Theory',
      'Customizable Difficulty Curves',
      'Advanced Analytics Engine',
      'Precision Scoring Models'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-purple-500 to-indigo-600'
  }
];
